import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateConfig } from "../../../../store/slices/User/SeasonalTheme/seasonalThemeSlice";
import { toast } from "react-toastify";
import { getSeasonalThemeImage, createSeasonalTheme } from "../api";
import { useGetSeasonalThemeBasicDataQuery } from "../../../../store/queries/User";
import { phoneRegExp } from "../../../../utils/Functions/table";

const useAddSeasonalTheme = (handleCreateClick, refetch) => {
  const dispatch = useDispatch();
  const [typeSlug, setTypeSlug] = useState("");
  const [isType, setType] = useState(null);
  const { data: basicData = {}, isLoading: basicLoading } =
    useGetSeasonalThemeBasicDataQuery();
  const {
    isEdit,
    imagePreview,
    imageName,
    arabicImagePreview,
    arabicImageName,
    editSeasonalThemeData,
    recapImagePreview,
    recapImageName,
    arabicRecapImageName,
    arabicRecapImagePreview,
  } = useSelector((state) => state.seasonalTheme);

  const {
    isLoadingImage,
    isLoading,
    isArabicImageLoading,
    isLoadingArabicRecapImage,
    isLoadingRecapImage,
  } = useSelector((state) => state.seasonalTheme);
  const validation = Yup.object({
    // theme_color: Yup.string().required("Please select theme color"),
    // text_color: Yup.string().required("Please select text color"),
    // menu_color: Yup.string().required("Please select menu color"),
    image: Yup.string().required("Please upload an image"),
    alias_image: Yup.string().required("Please upload an image"),
    recap_image: Yup.string().when("selected_type", {
      is: (val) => val?.id?.toString() === "5",
      then: (schema) => schema.required("Please upload an image"),
      otherwise: (schema) => schema.notRequired(),
    }),
    alias_recap_image: Yup.string().when("selected_type", {
      is: (val) => val?.id?.toString() === "5",
      then: (schema) => schema.required("Please upload an image"),
      otherwise: (schema) => schema.notRequired(),
    }),
    theme_type: Yup.object().required("Select theme type"),
    selected_type: Yup.object().required("Select type"),
    start_date: isType === 2 && Yup.string().required("Please enter from date"),
    end_date: isType === 2 && Yup.string().required("Please enter to date"),
    selected_item:
      basicData?.data?.dropdown_list?.hasOwnProperty(typeSlug) &&
      Yup.object().required("Select item"),
    add_user: Yup.string()
      .matches(phoneRegExp, "Please enter a valid phone number")
      .min(8, "Number should be at least 8 characters long")
      .max(8, "Number should be 8 characters long"),
    user_mobiles: Yup.array(),
  });

  const formik = useFormik({
    initialValues: {
      theme_color: "",
      text_color: "",
      // menu_color: "",
      image: "",
      alias_image: "",
      status: 2,
      theme_type: { id: 2, name: "Seasonal" } ?? "",
      start_date: "",
      end_date: "",
      selected_item: "",
      selected_type: "",
      add_user: "",
      user_mobiles: [],
      recap_image: "",
      alias_recap_image: "",
    },
    validationSchema: validation,
    onSubmit: (values) => {
      let data = {
        theme_color: values?.theme_color ?? "",
        text_color: values?.text_color ?? "",
        // menu_color: values?.menu_color ?? "",
        image: values?.image ?? "",
        alias_image: values?.alias_image ?? "",
        status: values?.status ?? 2,
        theme_type: values?.theme_type?.id ?? "",
        start_date: values?.start_date ?? "",
        end_date: values?.end_date ?? "",
        selected_item: values?.selected_item?._id ?? "",
        selected_type: values?.selected_type?.slug ?? "",
        user_mobiles: values?.user_mobiles,
        recap_image: values?.recap_image ?? "",
        alias_recap_image: values?.alias_recap_image ?? "",
      };
      if (values?.theme_type?.id !== 2) {
        delete data.start_date;
        delete data.end_date;
      }
      let newObj = Object.entries(data);
      let subData = newObj
        .filter(
          (item) => item[1] !== undefined && item[1] !== "" && item[1] !== null
        )
        .reduce((a, v) => ({ ...a, [v[0]]: v[1] }), {});
      dispatch(
        updateConfig((state) => {
          state.isLoading = true;
        })
      );

      if (!isEdit) {
        createSeasonalTheme(subData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(
              response?.data?.message || `Seasonal theme created successfully`
            );
            refetch();
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          } else if (
            response?.data?.status_code === 422 &&
            response?.data?.errors
          ) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
            });
            dispatch(
              updateConfig((state) => {
                state.isLoading = false;
              })
            );
          } else {
            toast.error(`Something went wrong!`);
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          }
        });
      } else {
        let updateSeasonalThemeData = {
          theme_id: editSeasonalThemeData?._id,
          ...subData,
        };

        createSeasonalTheme(updateSeasonalThemeData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Seasonal theme updated successfully`);
            refetch();
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
                state.editSeasonalThemeData = {};
              })
            );
          } else if (
            response?.data?.status_code === 422 &&
            response?.data?.errors
          ) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
            });
            dispatch(
              updateConfig((state) => {
                state.isLoading = false;
              })
            );
          } else {
            toast.error(`Something went wrong!`);
            handleCreateClick();
            dispatch(
              updateConfig((state) => {
                state.clearSelection = true;
                state.isLoading = false;
              })
            );
          }
        });
      }
    },
  });

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };
  const closeModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = false;
        state.isEdit = false;
        state.editSeasonalThemeData = {};
      })
    );
  };
  const handleImageChange = async (event, fieldName) => {
    const selectedLogo = event?.target?.files[0];

    // Map fieldName to corresponding loading state, preview state, and name state
    const fieldConfig = {
      image: {
        loadingKey: "isLoadingImage",
        previewKey: "imagePreview",
        nameKey: "imageName",
      },
      alias_image: {
        loadingKey: "isArabicImageLoading",
        previewKey: "arabicImagePreview",
        nameKey: "arabicImageName",
      },
      recap_image: {
        loadingKey: "isLoadingRecapImage",
        previewKey: "recapImagePreview",
        nameKey: "recapImageName",
      },
      alias_recap_image: {
        loadingKey: "isLoadingArabicRecapImage",
        previewKey: "arabicRecapImagePreview",
        nameKey: "arabicRecapImageName",
      },
    };

    const config = fieldConfig[fieldName];

    if (selectedLogo) {
      if (
        !["image/jpeg", "image/png", "image/jpg"].includes(selectedLogo.type)
      ) {
        formik.setFieldError(fieldName, "Only image files are allowed");
        return;
      }
      const formData = new FormData();
      formData.append("image", selectedLogo);
      dispatch(
        updateConfig((state) => {
          state[config.loadingKey] = true;
        })
      );
      const response = await getSeasonalThemeImage(formData);
      try {
        if (response?.data?.status_code === 200) {
          formik.setFieldValue(fieldName, response?.data?.data?.image_path);
        } else if (response?.status_code === 422 && response?.errors) {
          Object.keys(response?.errors).forEach((field) => {
            formik.setFieldError(fieldName, response?.errors[field]);
          });
        } else {
          toast.error(`Unable to add image, Please try again.`);
        }
      } catch (error) {
        console.error(`Unable to add image:`, error);
      } finally {
        dispatch(
          updateConfig((state) => {
            state[config.loadingKey] = false;
          })
        );
      }

      const file = selectedLogo;
      const reader = new FileReader();
      reader.onloadend = () => {
        dispatch(
          updateConfig((state) => {
            state[config.previewKey] = reader.result;
            state[config.nameKey] = selectedLogo?.name;
          })
        );
      };

      if (file) {
        reader.readAsDataURL(file);
      }
    }
  };

  useEffect(() => {
    let selectThemeType = basicData?.data?.content_types?.find(
      (item) => item?.id === editSeasonalThemeData?.theme_type
    );
    let selectVal = basicData?.data?.navigation_types?.find(
      (item) => item?.slug === editSeasonalThemeData?.selected_type
    );
    let typeVal = basicData?.data?.dropdown_list[
      editSeasonalThemeData?.selected_type
    ]?.find((item) => item?._id === editSeasonalThemeData?.selected_item);
     setTypeSlug(editSeasonalThemeData?.selected_type);
    if (isEdit && editSeasonalThemeData) {
      formik.setValues({
        status: editSeasonalThemeData?.status || 2,
        selected_type: selectVal,
        theme_type: selectThemeType,
        image: editSeasonalThemeData?.image,
        alias_image: editSeasonalThemeData?.alias_image,
        start_date: editSeasonalThemeData?.start_date,
        end_date: editSeasonalThemeData?.end_date,
        selected_item: typeVal,
        theme_color: editSeasonalThemeData?.theme_color,
        text_color: editSeasonalThemeData?.text_color,
        user_mobiles: editSeasonalThemeData?.user_mobiles,
        recap_image: editSeasonalThemeData?.recap_image,
        alias_recap_image: editSeasonalThemeData?.alias_recap_image,
        // menu_color: editSeasonalThemeData?.menu_color,
      });
      dispatch(
        updateConfig((state) => {
          state.imagePreview = editSeasonalThemeData?.image_full_url;
          state.imageName = editSeasonalThemeData?.image?.split("/").pop();
          state.arabicImagePreview =
            editSeasonalThemeData?.alias_image_full_url;
          state.arabicImageName = editSeasonalThemeData?.alias_image
            ?.split("/")
            .pop();
          state.recapImagePreview = editSeasonalThemeData?.recap_image_full_url;
          state.recapImageName = editSeasonalThemeData?.recap_image
            ?.split("/")
            .pop();
          state.arabicRecapImagePreview =
            editSeasonalThemeData?.alias_recap_image_full_url;
          state.arabicRecapImageName = editSeasonalThemeData?.alias_recap_image
            ?.split("/")
            .pop();
        })
      );
    }
    // eslint-disable-next-line
  }, [isEdit, editSeasonalThemeData, basicLoading]);

  const handleTypeChange = (e) => {
    formik?.setFieldValue("theme_type", e);
    setType(e?.id);
    if (!isEdit) {
      formik?.setFieldValue("start_date", "");
      formik?.setFieldValue("end_date", "");
    }
  };

  const handleSelectedTypeChange = (e) => {
    formik?.setFieldValue("selected_type", e);
    formik?.setFieldValue("selected_item", "");
    setTypeSlug(e?.slug);
    dispatch(
      updateConfig((state) => {
        state.recapImagePreview = "";
        state.recapImageName = "";
        state.arabicRecapImageName = "";
        state.arabicRecapImagePreview = "";
        state.isArabicImageLoading = false;
        state.isLoadingArabicRecapImage = false;
      })
    );
  };

  const getMinEndDate = () => {
    const startDate = formik?.values?.start_date;
    if (!startDate) return "";

    const date = new Date(startDate);
    date.setDate(date.getDate()); // Add one day

    return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  const getMaxDate = () => {
    const endDate = formik?.values?.end_date;
    if (!endDate) return "";

    const date = new Date(endDate);
    date.setDate(date.getDate());

    return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  const handleAddSelectedUser = () => {
    if (
      formik?.values?.add_user !== "" &&
      formik?.values?.add_user &&
      !formik?.errors?.add_user
    ) {
      if (!formik?.values?.user_mobiles?.includes(formik?.values?.add_user)) {
        formik.setFieldValue("user_mobiles", [
          ...formik.values.user_mobiles,
          formik?.values?.add_user,
        ]);
        formik.setFieldValue("add_user", "");
      } else {
        formik.setFieldError("add_user", "Number is already added");
      }
    }
  };

  const handleRemoveSelectedUser = (index) => {
    if (index > -1 && index < formik?.values?.user_mobiles.length) {
      const updatedUsers = [...formik.values.user_mobiles];
      updatedUsers.splice(index, 1);

      formik.setFieldValue("user_mobiles", updatedUsers);
    }
  };

  return {
    isEdit,
    formik,
    isLoading,
    imageName,
    basicData,
    imagePreview,
    editSeasonalThemeData,
    isLoadingImage,
    arabicImageName,
    arabicImagePreview,
    isArabicImageLoading,
    recapImageName,
    recapImagePreview,
    isLoadingRecapImage,
    arabicRecapImageName,
    arabicRecapImagePreview,
    isLoadingArabicRecapImage,
    handleAddSelectedUser,
    handleRemoveSelectedUser,
    handleImageChange,
    handleTypeChange,
    handleSelectedTypeChange,
    getFieldError,
    closeModal,
    getMinEndDate,
    getMaxDate,
    typeSlug,
  };
};

export default useAddSeasonalTheme;
